import { useEffect, useState } from "react";
import {
  fetchSettings,
  adminUpdateSettings,
  adminUploadImage,
  resolveImageUrl,
} from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import ItemListEditor from "../components/ItemListEditor";
import RichTextEditor from "../components/RichTextEditor";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Layout,
  GalleryHorizontal,
  Users,
  Mail,
  Type,
  Image as ImageIcon,
} from "lucide-react";

const BLOCK_META = {
  hero: { label: "Hero", icon: Layout, removable: false },
  marquee: { label: "Materiali (striscia)", icon: Type, removable: false },
  gallery: { label: "Galleria collezione", icon: GalleryHorizontal, removable: false },
  about: { label: "Chi siamo", icon: Users, removable: false },
  contact: { label: "Contatti", icon: Mail, removable: false },
  custom: { label: "Sezione personalizzata", icon: ImageIcon, removable: true },
};

const HERO_FIELDS = [
  ["hero_logo_image", "Immagine logo (visibile a tutto schermo all'apertura)", "image"],
  ["hero_eyebrow", "Eyebrow (visibile solo in modalità classica)"],
  ["hero_title", "Titolo testuale (usato se non c'è il logo)"],
  ["hero_subtitle", "Sottotitolo (sotto al logo, in corsivo)"],
  ["hero_description", "Descrizione", "richtext"],
  ["hero_cta", "Testo bottone"],
  ["hero_image", "Immagine di sfondo (solo se modalità 'immagine')", "image"],
];
const GALLERY_FIELDS = [
  ["gallery_eyebrow", "Eyebrow"],
  ["gallery_title_a", "Titolo riga 1"],
  ["gallery_title_b", "Titolo riga 2 (corsivo)"],
  ["gallery_text", "Descrizione", "textarea"],
];
const ABOUT_FIELDS = [
  ["about_eyebrow", "Eyebrow"],
  ["about_title_a", "Titolo riga 1"],
  ["about_title_b", "Titolo riga 2 (corsivo)"],
  ["about_text", "Testo", "richtext"],
];
const CONTACT_FIELDS = [
  ["contact_eyebrow", "Eyebrow"],
  ["contact_title_a", "Titolo riga 1"],
  ["contact_title_b", "Titolo riga 2 (corsivo)"],
  ["contact_text", "Descrizione", "richtext"],
  ["contact_address", "Indirizzo atelier"],
  ["contact_email", "Email"],
];

function Switch({ checked, onChange, testid, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-[color:var(--tr-accent)]" : "bg-[color:var(--tr-border)]"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function Field({ label, type, value, onChange, onUpload, testid }) {
  return (
    <div>
      <span className="label-eyebrow">{label}</span>
      {type === "textarea" ? (
        <textarea
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="tr-field !py-2 resize-none"
        />
      ) : type === "richtext" ? (
        <RichTextEditor
          testid={testid}
          value={value}
          onChange={onChange}
          rows={4}
        />
      ) : type === "image" ? (
        <div className="flex flex-wrap items-end gap-4">
          <input
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="tr-field !py-2 flex-1 min-w-[220px]"
            placeholder="https://… oppure carica un file"
          />
          <label className="tr-btn-ghost cursor-pointer">
            Carica file
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
          </label>
          {value && (
            <img
              src={resolveImageUrl(value)}
              alt="anteprima"
              className="w-20 h-20 object-cover border border-[color:var(--tr-border)]"
            />
          )}
        </div>
      ) : (
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="tr-field !py-2"
        />
      )}
    </div>
  );
}

export default function SettingsPanel() {
  const { reload } = useSettings();
  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchSettings().then((s) => {
      const next = {
        ...s,
        materials: (s.materials || []).join(", "),
        footer_items: s.footer_items || [],
        contact_items: s.contact_items || [],
        nav_links: s.nav_links || [],
        page_blocks: s.page_blocks && s.page_blocks.length
          ? s.page_blocks
          : [
              { id: "hero", type: "hero", enabled: true },
              { id: "marquee", type: "marquee", enabled: true },
              { id: "gallery", type: "gallery", enabled: true },
              { id: "about", type: "about", enabled: true },
              { id: "contact", type: "contact", enabled: true },
            ],
      };
      setForm({ ...next });
      setSavedForm(JSON.stringify(next));
    });
  }, []);

  const updateNavLink = (idx, patch) => {
    const list = [...(form?.nav_links || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm((f) => ({ ...f, nav_links: list }));
  };
  const moveNavLink = (idx, dir) => {
    const list = [...(form?.nav_links || [])];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setForm((f) => ({ ...f, nav_links: list }));
  };
  const removeNavLink = (idx) => {
    if (!window.confirm("Rimuovere questa voce del menu?")) return;
    const list = (form?.nav_links || []).filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, nav_links: list }));
  };
  const addNavLink = () => {
    const list = [...(form?.nav_links || []), { id: `n-${Date.now()}`, label: "Nuova voce", target: "", enabled: true }];
    setForm((f) => ({ ...f, nav_links: list }));
  };

  const dirty =
    savedForm !== null && form !== null && JSON.stringify(form) !== savedForm;

  // Warn before leaving the page with unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  if (!form) return <p className="font-body">Carico…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFor = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminUploadImage(file);
      set(key, url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Upload fallito");
    } finally {
      setBusy(false);
    }
  };

  // Block operations
  const updateBlock = (idx, patch) => {
    const next = [...form.page_blocks];
    next[idx] = { ...next[idx], ...patch };
    set("page_blocks", next);
  };
  const toggleBlock = (idx) =>
    updateBlock(idx, { enabled: !form.page_blocks[idx].enabled });
  const moveBlock = (idx, dir) => {
    const next = [...form.page_blocks];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    set("page_blocks", next);
  };
  const removeBlock = (idx) => {
    const b = form.page_blocks[idx];
    if (!BLOCK_META[b.type]?.removable) return;
    if (!window.confirm("Rimuovere questa sezione?")) return;
    set(
      "page_blocks",
      form.page_blocks.filter((_, i) => i !== idx)
    );
  };
  const addCustomBlock = () => {
    const id = `cs-${Date.now()}`;
    set("page_blocks", [
      ...form.page_blocks,
      {
        id,
        type: "custom",
        enabled: true,
        title: "Nuova sezione",
        body: "",
        image: "",
        layout: "image_text",
        background: "default",
      },
    ]);
    setExpanded(form.page_blocks.length);
  };
  const uploadBlockImage = (idx) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminUploadImage(file);
      updateBlock(idx, { image: url });
      toast.success("Immagine caricata");
    } catch {
      toast.error("Upload fallito");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      ...form,
      materials:
        typeof form.materials === "string"
          ? form.materials.split(",").map((s) => s.trim()).filter(Boolean)
          : form.materials,
      page_blocks: form.page_blocks || [],
      footer_items: form.footer_items || [],
      contact_items: form.contact_items || [],
      nav_links: form.nav_links || [],
    };
    try {
      await adminUpdateSettings(payload);
      await reload();
      setSavedForm(JSON.stringify(form));
      toast.success("Pagina aggiornata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const renderBlockEditor = (block, idx) => {
    if (block.type === "hero")
      return (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <span className="label-eyebrow">Stile Hero</span>
              <select
                data-testid="set-hero_layout"
                value={form.hero_layout || "logo"}
                onChange={(e) => set("hero_layout", e.target.value)}
                className="tr-field !py-2"
              >
                <option value="logo">Logo a tutto schermo</option>
                <option value="classic">Testo grande (classico)</option>
              </select>
            </div>
            <div>
              <span className="label-eyebrow">Sfondo</span>
              <select
                data-testid="set-hero_background_mode"
                value={form.hero_background_mode || "plain"}
                onChange={(e) => set("hero_background_mode", e.target.value)}
                className="tr-field !py-2"
              >
                <option value="plain">Plaster (sfondo caldo uniforme)</option>
                <option value="image">Immagine</option>
              </select>
            </div>
          </div>
          {HERO_FIELDS.map(([k, l, t]) => (
            <Field key={k} label={l} type={t} value={form[k]} onChange={(v) => set(k, v)} onUpload={uploadFor(k)} testid={`set-${k}`} />
          ))}
        </>
      );
    if (block.type === "gallery")
      return GALLERY_FIELDS.map(([k, l, t]) => (
        <Field key={k} label={l} type={t} value={form[k]} onChange={(v) => set(k, v)} testid={`set-${k}`} />
      ));
    if (block.type === "about")
      return ABOUT_FIELDS.map(([k, l, t]) => (
        <Field key={k} label={l} type={t} value={form[k]} onChange={(v) => set(k, v)} testid={`set-${k}`} />
      ));
    if (block.type === "contact")
      return (
        <>
          {CONTACT_FIELDS.map(([k, l, t]) => (
            <Field key={k} label={l} type={t} value={form[k]} onChange={(v) => set(k, v)} testid={`set-${k}`} />
          ))}
          <div>
            <span className="label-eyebrow">Dettagli di contatto</span>
            <p className="font-body text-sm text-[color:var(--tr-ink-soft)] mb-3">
              Email, telefono e indirizzi vengono trasformati automaticamente in link cliccabili.
            </p>
            <ItemListEditor
              items={form.contact_items}
              onChange={(v) => set("contact_items", v)}
              addLabel="Aggiungi dettaglio"
              testid="contact-items"
            />
          </div>
        </>
      );
    if (block.type === "marquee")
      return (
        <Field
          label="Materiali (separati da virgola)"
          value={form.materials}
          onChange={(v) => set("materials", v)}
          testid="set-materials"
        />
      );
    if (block.type === "custom")
      return (
        <>
          <Field
            label="Titolo"
            value={block.title}
            onChange={(v) => updateBlock(idx, { title: v })}
          />
          <Field
            label="Testo"
            type="richtext"
            value={block.body}
            onChange={(v) => updateBlock(idx, { body: v })}
          />
          <Field
            label="Immagine"
            type="image"
            value={block.image}
            onChange={(v) => updateBlock(idx, { image: v })}
            onUpload={uploadBlockImage(idx)}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <span className="label-eyebrow">Layout</span>
              <select
                data-testid={`block-${block.id}-layout`}
                value={block.layout || "image_text"}
                onChange={(e) => updateBlock(idx, { layout: e.target.value })}
                className="tr-field !py-2"
              >
                <option value="image_text">Immagine a sinistra + testo a destra</option>
                <option value="text_image">Testo a sinistra + immagine a destra</option>
                <option value="text_centered">Solo testo, centrato</option>
                <option value="text_only">Solo testo, allineato</option>
                <option value="image_only">Solo immagine (panoramica)</option>
              </select>
            </div>
            <div>
              <span className="label-eyebrow">Sfondo</span>
              <select
                data-testid={`block-${block.id}-background`}
                value={block.background || "default"}
                onChange={(e) => updateBlock(idx, { background: e.target.value })}
                className="tr-field !py-2"
              >
                <option value="default">Chiaro</option>
                <option value="soft">Sabbia (più caldo)</option>
                <option value="dark">Scuro</option>
              </select>
            </div>
          </div>
        </>
      );
    return null;
  };

  return (
    <div data-testid="settings-panel" className="space-y-6 pb-32">
      {/* Maintenance mode */}
      <div
        data-testid="maintenance-card"
        className={`border px-5 py-5 space-y-5 transition-colors ${
          form.maintenance_mode
            ? "border-[color:var(--tr-accent)] bg-[color:var(--tr-bg-2)]"
            : "border-[color:var(--tr-border)] bg-[color:var(--tr-bg)]"
        }`}
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="label-eyebrow">Modalità lavori in corso</p>
            <p className="font-body text-sm text-[color:var(--tr-ink-soft)] mt-1 max-w-xl">
              Quando è attiva, i visitatori vedono solo la schermata iniziale con il logo: menu, sezioni e piè di pagina sono nascosti.
              Tu, da amministratore connesso, continui a vedere il sito completo per l'anteprima.
            </p>
          </div>
          <Switch
            checked={!!form.maintenance_mode}
            onChange={(v) => set("maintenance_mode", v)}
            testid="set-maintenance_mode"
            label="Modalità lavori in corso"
          />
        </div>
        <Field
          label="Messaggio sulla schermata iniziale (lascia vuoto per non mostrarlo)"
          value={form.maintenance_message}
          onChange={(v) => set("maintenance_message", v)}
          testid="set-maintenance_message"
        />
      </div>

      {/* Site identity */}
      <div className="border border-[color:var(--tr-border)] bg-[color:var(--tr-bg)] px-5 py-5 space-y-5">
        <p className="label-eyebrow">Identità sito</p>
        <p className="font-body text-sm text-[color:var(--tr-ink-soft)]">
          Titolo che appare nella tab del browser e descrizione usata per condivisioni e motori di ricerca.
        </p>
        <Field
          label="Titolo del sito (tab del browser)"
          value={form.site_title}
          onChange={(v) => set("site_title", v)}
          testid="set-site_title"
        />
        <Field
          label='Wordmark (scritta del logo nella barra, es. "tramæ")'
          value={form.wordmark}
          onChange={(v) => set("wordmark", v)}
          testid="set-wordmark"
        />
        <p className="font-body text-xs text-[color:var(--tr-ink-soft)] -mt-3">
          Suggerimento: per la legatura "ae" copia e incolla il carattere <code>æ</code>. Ad esempio: <code>tramæ</code>.
        </p>
        <Field
          label="Descrizione (SEO / condivisione)"
          type="textarea"
          value={form.site_description}
          onChange={(v) => set("site_description", v)}
          testid="set-site_description"
        />
      </div>

      {/* Navigation editor */}
      <div className="border border-[color:var(--tr-border)] bg-[color:var(--tr-bg)] px-5 py-5 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="label-eyebrow">Menu di navigazione</p>
            <p className="font-body text-sm text-[color:var(--tr-ink-soft)] mt-1">
              Aggiungi, rinomina, riordina o nascondi le voci dell'header. Il "target" è l'ID di una sezione (es. <code>collezione</code>, <code>chi-siamo</code>, <code>contatti</code>) oppure un URL completo (https://…).
            </p>
          </div>
          <button
            data-testid="nav-add"
            onClick={addNavLink}
            className="tr-btn-ghost inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Aggiungi voce
          </button>
        </div>
        <div className="space-y-3">
          {(form.nav_links || []).map((l, idx) => (
            <div
              key={l.id}
              data-testid={`nav-row-${l.id}`}
              className={`border ${l.enabled === false ? "border-dashed opacity-60" : ""} border-[color:var(--tr-border)] p-4`}
            >
              <div className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-12 sm:col-span-4">
                  <span className="label-eyebrow">Etichetta</span>
                  <input
                    value={l.label || ""}
                    onChange={(e) => updateNavLink(idx, { label: e.target.value })}
                    placeholder="Es. Servizi"
                    className="tr-field !py-1.5"
                  />
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <span className="label-eyebrow">Target</span>
                  <input
                    value={l.target || ""}
                    onChange={(e) => updateNavLink(idx, { target: e.target.value })}
                    placeholder="collezione | chi-siamo | https://..."
                    className="tr-field !py-1.5"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3 flex items-center gap-1 sm:justify-end">
                  <button onClick={() => moveNavLink(idx, -1)} disabled={idx === 0} className="p-2 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-25" aria-label="Su"><ChevronUp className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button onClick={() => moveNavLink(idx, 1)} disabled={idx === (form.nav_links?.length || 0) - 1} className="p-2 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-25" aria-label="Giù"><ChevronDown className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button onClick={() => updateNavLink(idx, { enabled: l.enabled === false })} className="p-2 hover:bg-[color:var(--tr-bg-2)]" aria-label={l.enabled === false ? "Mostra" : "Nascondi"}>
                    {l.enabled === false ? <EyeOff className="w-4 h-4 text-[color:var(--tr-ink-soft)]" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                  <button onClick={() => removeNavLink(idx)} className="p-2 hover:bg-red-50 text-red-700" aria-label="Elimina">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4 mb-2 mt-10">
        <div>
          <p className="label-eyebrow mb-2">Struttura della pagina</p>
          <p className="font-body text-[color:var(--tr-ink-soft)] max-w-xl">
            Riordina le sezioni con le frecce, nascondile con l'occhio, modificale aprendole.
            Aggiungi nuove sezioni con il bottone in fondo.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[0.78rem] font-sub uppercase tracking-[0.22em] text-[color:var(--tr-ink-soft)]">
          <a href="/" target="_blank" rel="noreferrer" className="hover:text-[color:var(--tr-accent)]">
            Anteprima sito ↗
          </a>
        </div>
      </div>

      {form.page_blocks.map((block, idx) => {
        const meta = BLOCK_META[block.type] || BLOCK_META.custom;
        const Icon = meta.icon;
        const isOpen = expanded === idx;
        const isFirst = idx === 0;
        const isLast = idx === form.page_blocks.length - 1;
        return (
          <div
            key={block.id}
            data-testid={`block-${block.id}`}
            className={`border bg-[color:var(--tr-bg)] transition-opacity ${
              block.enabled ? "border-[color:var(--tr-border)] opacity-100" : "border-dashed border-[color:var(--tr-border)] opacity-60"
            }`}
          >
            <div className="flex items-center gap-3 px-4 sm:px-5 py-4">
              <button
                onClick={() => setExpanded(isOpen ? null : idx)}
                className="flex items-center gap-3 flex-1 text-left group"
                data-testid={`block-toggle-${block.id}`}
              >
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  strokeWidth={1.5}
                />
                <Icon className="w-4 h-4 text-[color:var(--tr-ink-soft)]" strokeWidth={1.5} />
                <span className="font-display text-xl">
                  {block.type === "custom" && block.title ? block.title : meta.label}
                </span>
                {!block.enabled && (
                  <span className="label-eyebrow ml-2" style={{ color: "#A8A39A" }}>
                    Nascosta
                  </span>
                )}
              </button>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveBlock(idx, -1)}
                  disabled={isFirst}
                  className="p-2 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-25 disabled:cursor-not-allowed"
                  aria-label="Sposta su"
                  data-testid={`block-up-${block.id}`}
                >
                  <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => moveBlock(idx, 1)}
                  disabled={isLast}
                  className="p-2 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-25 disabled:cursor-not-allowed"
                  aria-label="Sposta giù"
                  data-testid={`block-down-${block.id}`}
                >
                  <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => toggleBlock(idx)}
                  className="p-2 hover:bg-[color:var(--tr-bg-2)]"
                  aria-label={block.enabled ? "Nascondi" : "Mostra"}
                  title={block.enabled ? "Nascondi" : "Mostra"}
                  data-testid={`block-visibility-${block.id}`}
                >
                  {block.enabled ? (
                    <Eye className="w-4 h-4" strokeWidth={1.5} />
                  ) : (
                    <EyeOff className="w-4 h-4 text-[color:var(--tr-ink-soft)]" strokeWidth={1.5} />
                  )}
                </button>
                {meta.removable && (
                  <button
                    onClick={() => removeBlock(idx)}
                    className="p-2 hover:bg-red-50 text-red-700"
                    aria-label="Elimina"
                    data-testid={`block-delete-${block.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>

            {isOpen && (
              <div className="border-t border-[color:var(--tr-border)] bg-[color:var(--tr-bg-2)] px-5 py-6 space-y-5">
                {renderBlockEditor(block, idx)}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-3">
        <button
          data-testid="add-custom-block"
          onClick={addCustomBlock}
          className="tr-btn-ghost inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Aggiungi sezione
        </button>
      </div>

      {/* Footer (always present, no block) */}
      <div className="border border-[color:var(--tr-border)] bg-[color:var(--tr-bg)] px-5 py-5 space-y-5">
        <div className="flex items-center justify-between gap-6">
          <p className="label-eyebrow">Piè di pagina</p>
          <div className="flex items-center gap-3">
            <span className="label-eyebrow" data-testid="footer-visibility-label">
              {form.footer_enabled !== false ? "Visibile" : "Nascosto"}
            </span>
            <Switch
              checked={form.footer_enabled !== false}
              onChange={(v) => set("footer_enabled", v)}
              testid="set-footer_enabled"
              label="Mostra piè di pagina"
            />
          </div>
        </div>
        <p className="font-body text-sm text-[color:var(--tr-ink-soft)]">
          Aggiungi, rinomina o nascondi le voci. Email e telefono diventano automaticamente cliccabili.
        </p>
        <ItemListEditor
          items={form.footer_items}
          onChange={(v) => set("footer_items", v)}
          addLabel="Aggiungi voce piè di pagina"
          testid="footer-items"
        />
      </div>

      <div className="fixed bottom-0 left-0 right-0 px-6 sm:px-12 py-4 bg-[color:var(--tr-bg)]/95 backdrop-blur-sm border-t border-[color:var(--tr-border)] z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              data-testid="dirty-indicator"
              className={`inline-block w-2 h-2 rounded-full transition-colors ${
                dirty ? "" : "opacity-40"
              }`}
              style={{ backgroundColor: dirty ? "#B45309" : "#9E8B65" }}
            />
            <p className="label-eyebrow">
              {dirty
                ? "Modifiche non salvate — clicca «Salva pagina»"
                : "Tutto salvato"}
            </p>
          </div>
          <button
            data-testid="settings-save"
            onClick={save}
            disabled={busy || !dirty}
            className="tr-btn disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Salvo…" : "Salva pagina"}
          </button>
        </div>
      </div>
    </div>
  );
}
